import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { updateMe } from '../../../lib/api';

const AccountTab = () => {
  const { user, token } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [emailPassword, setEmailPassword] = useState('');
  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const emailChanged = email && email !== user?.email;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const payload = {};

      // Email change (requires password)
      if (emailChanged) {
        if (!emailPassword) {
          setMessage({ type: 'error', text: 'Password is required to change your email' });
          setSaving(false);
          return;
        }
        payload.email = email;
        payload.currentPassword = emailPassword;
      }

      // Name change
      if (name && name !== user?.name) payload.name = name;

      // Password change
      if (newPassword) {
        if (!currentPassword) {
          setMessage({ type: 'error', text: 'Current password is required to set a new password' });
          setSaving(false);
          return;
        }
        payload.currentPassword = currentPassword;
        payload.newPassword = newPassword;
      }

      if (Object.keys(payload).length === 0) {
        setMessage({ type: 'info', text: 'No changes to save' });
        setSaving(false);
        return;
      }

      await updateMe(token, payload);
      setMessage({ type: 'success', text: 'Profile updated successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setEmailPassword('');
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div
        className="bg-white rounded-sm p-8"
        style={{ border: '1px solid rgba(212,175,55,0.4)', boxShadow: '0 4px 20px rgba(184,134,11,0.08)' }}
      >
        <h3
          className="text-lg font-bold text-[#36454F] mb-6"
          style={{ fontFamily: "'Cinzel', serif" }}
        >
          Account Information
        </h3>

        <form onSubmit={handleSave} className="space-y-5 max-w-md">
          {message && (
            <div
              className={`px-4 py-3 rounded text-sm ${
                message.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : message.type === 'error'
                  ? 'bg-red-50 border border-red-200 text-red-700'
                  : 'bg-blue-50 border border-blue-200 text-blue-700'
              }`}
            >
              {message.text}
            </div>
          )}

          {/* Email */}
          <div className="space-y-1.5">
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#54626F]">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-10 px-3 bg-white border border-[#E6D8AD] rounded text-sm text-[#36454F] focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37]"
              style={{ fontFamily: "'Lato', sans-serif" }}
            />
            {emailChanged && (
              <div className="mt-2 space-y-1.5">
                <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#54626F]">
                  Confirm with Password
                </label>
                <input
                  type="password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  placeholder="Enter your password to confirm"
                  className="w-full h-10 px-3 bg-white border border-[#E6D8AD] rounded text-sm text-[#36454F] focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] placeholder:text-[#54626F]/40"
                  style={{ fontFamily: "'Lato', sans-serif" }}
                />
                <p className="text-[9px] text-[#54626F]/70">Your current password is required to change email</p>
              </div>
            )}
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#54626F]">
              Display Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full h-10 px-3 bg-white border border-[#E6D8AD] rounded text-sm text-[#36454F] focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] placeholder:text-[#54626F]/40"
              style={{ fontFamily: "'Lato', sans-serif" }}
            />
          </div>

          {/* Divider */}
          <div className="border-t border-[#E6D8AD]/50 pt-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#54626F] mb-4">
              Change Password
            </p>
          </div>

          {/* Current Password */}
          <div className="space-y-1.5">
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#54626F]">
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              className="w-full h-10 px-3 bg-white border border-[#E6D8AD] rounded text-sm text-[#36454F] focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] placeholder:text-[#54626F]/40"
              style={{ fontFamily: "'Lato', sans-serif" }}
            />
          </div>

          {/* New Password */}
          <div className="space-y-1.5">
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#54626F]">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className="w-full h-10 px-3 bg-white border border-[#E6D8AD] rounded text-sm text-[#36454F] focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] placeholder:text-[#54626F]/40"
              style={{ fontFamily: "'Lato', sans-serif" }}
            />
          </div>

          {/* Save Button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={saving}
              className="h-10 px-8 rounded text-white text-xs font-bold uppercase tracking-widest transition-transform active:scale-95 disabled:opacity-50 flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #E5C56A 0%, #C5A059 100%)',
                border: '1px solid #B8860B',
              }}
            >
              {saving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountTab;
